import React from "react";
import { Helmet } from "react-helmet-async";
import Breadcrumbs from "../components/Breadcrumbs";
import { staticMeta } from "../lib/seo";

/**
 * /privacy — plain-language privacy notice.
 * Per owner spec (SEO pass §7): Google sign-in data (email/name/avatar),
 * what's stored, public display of uploads, cookies/localStorage for
 * auth, deletion via support@cardcapture.show. Generic-but-accurate;
 * invent nothing.
 */
export default function PrivacyPage() {
  const meta = staticMeta("privacy");
  return (
    <div
      className="mx-auto w-full max-w-[900px] px-5 py-10 md:px-gutter md:py-14"
      data-testid="privacy-page"
    >
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta name="robots" content={meta.robots} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:site_name" content="CardCapture" />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content={meta.ogType} />
        <meta property="og:image" content={meta.ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.ogImage} />
      </Helmet>

      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Privacy" }]} />

      <div className="mt-6 text-eyebrow uppercase text-indigo-text">
        Privacy
      </div>
      <h1 className="mt-1 text-title-page text-ink-text">Privacy Policy</h1>

      <div className="mt-6 flex flex-col gap-6 text-body text-ink-muted">
        <section>
          <h2 className="text-title-section text-ink-text">What we collect</h2>
          <p className="mt-2">
            Signing in to CardCapture uses Google Sign-In. We receive your
            Google account email, display name, and avatar URL. We do not
            request, receive, or store any other Google profile fields.
          </p>
        </section>

        <section>
          <h2 className="text-title-section text-ink-text">What we store</h2>
          <p className="mt-2">
            Your email, display name, avatar URL, and (optionally) a
            username you choose are stored in our database and are used to
            attribute your submissions and votes. Any image or video you
            upload is stored in cloud object storage and served through
            our public media CDN so that anyone browsing the site — signed
            in or not — can see it on the card page you attached it to.
          </p>
        </section>

        <section>
          <h2 className="text-title-section text-ink-text">Cookies and local storage</h2>
          <p className="mt-2">
            We use browser local storage to remember that you are signed
            in and to keep your session token. We do not use third-party
            advertising cookies. We do not sell or share your data with
            third parties.
          </p>
        </section>

        <section>
          <h2 className="text-title-section text-ink-text">Public display</h2>
          <p className="mt-2">
            Approved submissions appear publicly on the relevant card page
            and are indexed by search engines. Your public profile shows
            your display name (or username, if you set one) and your
            total community upvotes; it does not show your email address
            or admin status.
          </p>
        </section>

        <section>
          <h2 className="text-title-section text-ink-text">Deletion</h2>
          <p className="mt-2">
            To delete your account or a specific submission, contact
            <a
              href="mailto:support@cardcapture.show"
              className="ml-1 text-indigo-text hover:underline"
              data-testid="privacy-support-email"
            >
              support@cardcapture.show
            </a>
            . We will remove your account, submissions, and votes from
            our systems within a reasonable time frame.
          </p>
        </section>

        <section>
          <h2 className="text-title-section text-ink-text">Contact</h2>
          <p className="mt-2">
            Questions about this policy? Email support@cardcapture.show.
          </p>
        </section>
      </div>
    </div>
  );
}
